import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';

import { useChatStore } from '../useChatStore';
import { inferModuleFromPathname } from '../utils/inferModuleFromPathname';
import { AGENT_STARTERS, startersToChatOptions } from './staticStarters';
import { useDynamicStarters } from './useDynamicStarters';

export const useChatOptions = (
  sendMessage: (message: string) => Promise<void>
) => {
  const {
    messages,
    sessionInitialized,
    isInitialising,
    chatMode,
    setOptions,
    clearOptions,
  } = useChatStore();

  const isAgent = chatMode === 'agent';

  const { pathname } = useLocation();
  const { user } = useRisksmartUser();
  const module = inferModuleFromPathname(pathname);
  const { starters, source } = useDynamicStarters(module, user?.orgRole);

  // Agent mode uses its own hardcoded starters (no dynamic endpoint yet);
  // knowledge mode uses the contextual dynamic starters with a static fallback.
  const options = useMemo(
    () =>
      isAgent
        ? startersToChatOptions(AGENT_STARTERS, sendMessage, 'static')
        : startersToChatOptions(starters, sendMessage, source),
    [isAgent, starters, sendMessage, source]
  );

  const lastSetIdsRef = useRef<string>('');

  useEffect(() => {
    // Agent mode shows its starters alongside the empty-state welcome intro
    // (gated on an empty transcript, no seeded system message), so both vanish
    // on the first send and return on "New chat". Platform shows its starters
    // once its welcome system message exists and the session is initialised.
    // Either way the chips are cleared on the first send via clearOptions().
    const hasSystemMessage = messages.some((message) => message.isSystem);
    const shouldShowOptions = isAgent
      ? messages.length === 0 && !isInitialising
      : hasSystemMessage && sessionInitialized && !isInitialising;

    if (!shouldShowOptions) {
      if (lastSetIdsRef.current) {
        clearOptions();
        lastSetIdsRef.current = '';
      }

      return;
    }

    const ids = options.map((o) => o.id).join('|');
    if (ids !== lastSetIdsRef.current) {
      setOptions(options);
      lastSetIdsRef.current = ids;
    }
  }, [
    isAgent,
    messages,
    sessionInitialized,
    isInitialising,
    options,
    setOptions,
    clearOptions,
  ]);
};
